/**
 * Task D2 — one job: its counts, its timeline, and every SIM in it.
 *
 * The live state comes from `useJob(jobRef)`, which polls on `terminal` rather
 * than on a status list and stops of its own accord. The re-check control is
 * wired straight to that hook's `refresh()` — `POST /jobs/{ref}/refresh`, the
 * §10.4 escape hatch out of `EXPIRED`. It is rate-limited to 6/min/user
 * server-side, so a 429 surfaces as an error rather than being retried.
 *
 * **`PARTIAL` is the case this page is really for** (criterion 20). Most of the
 * job worked, some of it did not, and the only useful next step is the list of
 * which SIMs. So the orders table is always on this page and always has a jump
 * link pointing at it, rather than being a tab someone has to know about. The
 * *tone* of a partial job — warning, not error — is `describeJob()`'s decision
 * rendered through `JobStatusBadge`; nothing here re-derives it.
 */

import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useEffect, type ReactElement } from 'react';
import { Link, useLocation, useParams } from 'react-router';

import { BackLink } from '../../components/BackLink.js';
import { ErrorState } from '../../components/ErrorState.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Skeleton } from '../../components/Skeleton.js';
import { jobQueryKey, useJob } from '../../lib/useJob.js';

import { JobOrdersTable } from './JobOrdersTable.js';
import { JobStatusBadge } from './JobStatusBadge.js';

/** The anchor the "which SIMs" link points at, and the table's own heading id. */
const ORDERS_ANCHOR = 'job-orders';

/**
 * Jump to the orders table.
 *
 * A hash in a `<Link>` is not a jump. React Router intercepts the click and
 * pushes the location itself, so the browser's native "scroll to the element
 * with this id" never runs: the URL grew a `#job-orders` and the page did not
 * move a pixel. On a PARTIAL job the whole point of the link is to get an
 * operator to the list of which SIMs failed, so a link that silently does
 * nothing is the failure mode this page exists to avoid.
 *
 * Focus moves with the scroll. A jump link that only scrolls leaves a keyboard
 * user's focus where it was, so their next Tab continues from the top of the
 * page they just left — the target takes `tabIndex={-1}` to be able to receive
 * it, exactly as the shell's "Skip to content" target does.
 */
function jumpToOrders(): void {
  const target = document.getElementById(ORDERS_ANCHOR);
  if (!target) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  target.focus({ preventScroll: true });
}

/**
 * The label above a band on this page.
 *
 * One class string rather than three copies of the same six utilities, because
 * "Counts" and "Timeline" label the bands under them in exactly the way a
 * column header labels a column — and the moment two of them disagree about
 * their size, the page has two ranks of section heading and no way to tell
 * which is which.
 */
const BAND_LABEL =
  'mb-[var(--space-3)] text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted';

function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '—' : at.toLocaleString();
}

export function JobDetailPage(): ReactElement {
  const { jobRef } = useParams<{ jobRef: string }>();
  const ref = jobRef ?? null;

  const { job, refresh, isRefreshing, error } = useJob(ref);
  const queryClient = useQueryClient();

  /*
    The same jump, for a URL that arrives with the hash already on it — the
    link an operator pasted into a support thread to say "look at these SIMs".
    It waits for the job, because until then the heading it scrolls to has not
    been rendered and there is nothing to find.
  */
  const { hash } = useLocation();
  const loaded = job !== null && job !== undefined;
  useEffect(() => {
    if (!loaded || hash !== `#${ORDERS_ANCHOR}`) return;
    jumpToOrders();
  }, [loaded, hash]);

  if (error) {
    return (
      <>
        <PageHeader title="Job" description={ref ?? undefined} />
        <ErrorState
          error={error}
          // Re-reading the job, not re-asking the network about it: the read
          // failed, and `refresh()` is a rate-limited upstream call that would
          // be the wrong instrument for a failed GET.
          onRetry={() => {
            void queryClient.invalidateQueries({ queryKey: jobQueryKey(ref ?? '') });
          }}
        />
      </>
    );
  }

  if (!job) {
    return (
      <>
        <PageHeader title="Job" eyebrow="Jobs" description={ref ?? undefined} />
        {/* The shape of the page it replaces — a state panel, a band of five
            counts, then the table — so nothing jumps when the job lands. */}
        <div className="flex flex-col gap-[var(--space-8)]">
          <Skeleton className="h-[5.5rem] w-full" />
          <Skeleton className="h-[6.5rem] w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </>
    );
  }

  const needAttention = job.counts.failed + job.counts.rejected;

  return (
    <>
      {/*
        The eyebrow says which screen this is a detail OF, which is the one
        thing "Job" over a 26-character ref does not. The ref stays the
        description: it is the whole identity of the record and it is what an
        operator pastes into a support thread.
      */}
      <BackLink fallback="/jobs" fallbackLabel="Jobs" />
      <PageHeader title="Job" eyebrow="Jobs" description={job.ref} />

      <section aria-labelledby="job-state-heading" className="mb-[var(--space-8)]">
        <h2 id="job-state-heading" className="sr-only">
          Current state
        </h2>
        {/*
          `.card`, not a hand-rolled `--brand-radius` box on `border-line`. The
          three panels on this page used a lighter border than every other
          framed object in the app and a radius token nothing else reads, so
          they read as a different kind of thing from the table directly below
          them — which they are not.
        */}
        <div className="card p-[var(--space-5)]">
          <JobStatusBadge job={job} onRecheck={() => void refresh()} rechecking={isRefreshing} />
          <p className="mt-[var(--space-4)] border-t border-line pt-[var(--space-3)]">
            {/* The jump link is the page's main move after a PARTIAL, so it is
                shaped like the dashboard's "View all activity" — a chevron
                pointing at where it lands, not an underlined sentence. */}
            <Link
              to={`#${ORDERS_ANCHOR}`}
              onClick={jumpToOrders}
              className="inline-flex items-center gap-1 text-[length:var(--text-sm)] font-medium text-primary underline-offset-[3px] hover:underline focus-visible:underline"
            >
              See the outcome for each SIM
              {needAttention > 0 ? ` — ${String(needAttention)} need attention` : ''}
              <ChevronRight aria-hidden="true" size={14} strokeWidth={2} className="shrink-0" />
            </Link>
          </p>
        </div>
      </section>

      <section aria-labelledby="job-counts-heading" className="mb-[var(--space-8)]">
        <h2 id="job-counts-heading" className={BAND_LABEL}>
          Counts
        </h2>
        {/*
          One frame with dividers, the same object as the dashboard's stat band
          — five figures that are five parts of one total read as a band, and as
          five separate cards read as five unrelated facts.
        */}
        <div className="card grid grid-cols-2 divide-x divide-y divide-line overflow-hidden sm:grid-cols-3 lg:grid-cols-5 lg:divide-y-0">
          <Count label="SIMs in this job" value={job.counts.total} />
          <Count label="Succeeded" value={job.counts.succeeded} />
          <Count label="Failed" value={job.counts.failed} />
          <Count label="Rejected" value={job.counts.rejected} />
          <Count label="Still running" value={job.counts.inProgress} />
        </div>
      </section>

      <section aria-labelledby="job-timeline-heading" className="mb-[var(--space-8)]">
        <h2 id="job-timeline-heading" className={BAND_LABEL}>
          Timeline
        </h2>
        <ol className="card flex flex-col p-[var(--space-5)]">
          <TimelineStep label="Submitted to the network" at={job.submittedAt} />
          <TimelineStep
            label={job.terminal ? 'Finished' : 'Still running'}
            at={job.completedAt}
            last
          />
        </ol>
      </section>

      <section aria-labelledby={ORDERS_ANCHOR}>
        <h2
          id={ORDERS_ANCHOR}
          tabIndex={-1}
          className="mb-[var(--space-3)] scroll-mt-[calc(var(--topbar-h)+var(--space-4))] text-[length:var(--text-lg)] font-semibold tracking-[var(--tracking-tight)] text-body focus-visible:outline-none"
        >
          Every SIM in this job
        </h2>
        <JobOrdersTable jobRef={job.ref} />
      </section>
    </>
  );
}

/**
 * One cell of the counts band.
 *
 * The figure takes its rank from air and tracking rather than from a larger
 * size, which is the same argument the dashboard's stat tile makes: §16.2 stops
 * the type scale at `--text-2xl` because a console is read, not browsed. And
 * `tabular-nums` is deliberately absent for the same reason it is absent there
 * — tabular figures give every digit a zero's width, which reads loose at 24px.
 * They belong in the table below, where digits genuinely align down a column.
 */
function Count({ label, value }: { label: string; value: number }): ReactElement {
  return (
    <div className="flex flex-col px-[var(--space-5)] py-[var(--space-4)]">
      <p className="truncate text-[length:var(--text-2xs)] font-medium uppercase tracking-[var(--tracking-caps)] text-muted">
        {label}
      </p>
      <p className="mt-[var(--space-2)] text-[length:var(--text-2xl)] font-semibold leading-[var(--leading-display)] tracking-[var(--tracking-tight)] text-body">
        {value.toLocaleString()}
      </p>
    </div>
  );
}

/**
 * A step on the rail.
 *
 * **The dot encodes whether the step has a timestamp, and nothing else** — the
 * same discipline the dashboard's activity rail keeps. It is tempting to colour
 * it by outcome, and wrong: every rule about how a job's state reads lives in
 * `describeJob()` and criterion 18 pins that function to `JobStatusBadge`. A
 * second, local reading here is exactly the drift that criterion exists to
 * stop. `at` is a field the DTO states outright, so the dot claims only what it
 * can know: this moment has happened, or it has not.
 *
 * The mark is `chart-idle`, the platform's fixed neutral. A tenant colour on a
 * timeline dot would be §16.7 ruling A broken — a mark never follows the brand.
 */
function TimelineStep({
  label,
  at,
  last = false,
}: {
  label: string;
  at: string | null;
  /** The rail stops at the last dot: a line past it implies a step after it. */
  last?: boolean;
}): ReactElement {
  return (
    <li className={`relative flex gap-[var(--space-3)] ${last ? '' : 'pb-[var(--space-5)]'}`}>
      {last ? null : (
        <span
          aria-hidden="true"
          className="absolute bottom-0 left-[0.1875rem] top-[0.75rem] w-px bg-line"
        />
      )}
      <span
        aria-hidden="true"
        className={`relative mt-[0.3125rem] size-[0.4375rem] shrink-0 rounded-[var(--radius-pill)] ${
          at === null ? 'bg-surface ring-1 ring-line-strong' : 'bg-chart-idle'
        }`}
      />
      <div className="flex min-w-0 flex-col">
        <span className="text-[length:var(--text-sm)] font-medium text-body">{label}</span>
        <span className="text-[length:var(--text-sm)] tabular-nums text-muted">
          {formatWhen(at)}
        </span>
      </div>
    </li>
  );
}
